import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebaseConfig';
import { useApp } from '../context/AppContext';
import { useFriendLink } from '../hooks/useFriendLink';
import ConnectionRow from '../components/ConnectionRow';
import SettingsSheet from '../components/SettingsSheet';
import styles from './FriendsScreen.module.css';

const removeConnectionFn = httpsCallable(functions, 'removeConnection');

export default function FriendsScreen({ onViewFriendFeed }) {
  const { state } = useApp();
  const { friends } = state;
  const { shareUrl, loading: linkLoading } = useFriendLink();
  const [showSettings, setShowSettings] = useState(false);
  const [copied, setCopied] = useState(false);
  const [removing, setRemoving] = useState(null);

  const handleCopy = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      await navigator.share({ title: 'Join my creativity feed', url: shareUrl });
    } else {
      handleCopy();
    }
  };

  const handleRemove = async (friend) => {
    if (!window.confirm(`Remove ${friend.displayName}? You'll no longer see each other's entries.`)) return;
    setRemoving(friend.userId);
    try {
      await removeConnectionFn({ friendUserId: friend.userId });
    } catch (err) {
      console.warn('Remove connection error:', err);
    }
    setRemoving(null);
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.heading}>Friends</h2>
        <button className={styles.gearBtn} onClick={() => setShowSettings(true)}>⚙️</button>
      </header>

      <section className={styles.card}>
        <div className={styles.sectionLabel}>Your invite link</div>
        <div className={styles.linkBox}>
          {linkLoading ? '…' : shareUrl}
        </div>
        <div className={styles.linkActions}>
          <button className={styles.copyBtn} onClick={handleCopy} disabled={linkLoading}>
            {copied ? 'Copied!' : 'Copy link'}
          </button>
          <button className={styles.shareBtn} onClick={handleShare} disabled={linkLoading}>
            Share ↗
          </button>
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.sectionLabel}>
          Connections ({friends.length})
        </div>

        {friends.length === 0 ? (
          <p className={styles.emptyConnections}>
            No connections yet — share your invite link to get started.
          </p>
        ) : (
          <div className={styles.connectionList}>
            {friends.map(friend => (
              <div key={friend.userId} className={styles.connectionItem}>
                {removing === friend.userId ? (
                  <div className={styles.removingRow}>Removing…</div>
                ) : (
                  <ConnectionRow
                    friend={friend}
                    onViewFeed={onViewFriendFeed}
                    onRemove={handleRemove}
                  />
                )}
                <div className={styles.separator} />
              </div>
            ))}
          </div>
        )}
      </section>

      {showSettings && <SettingsSheet onClose={() => setShowSettings(false)} />}
    </div>
  );
}
